import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL } from '../../config';
import { shouldSkipCachedFeed } from '../../utils/feedFreshness';
import { isNetworkOffline } from '../../utils/requireNetwork';

export const fetchBetHouseDetail = createAsyncThunk(
  "betHouse/fetchBetHouseDetail",
  async ({ betId, userId }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (userId) params.set("userId", userId);
      const qs = params.toString();
      const response = await fetch(
        `${API_BASE_URL}/api/bets/${encodeURIComponent(betId)}/detail${qs ? `?${qs}` : ""}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue({
          betId: String(betId),
          status: response.status,
          message: data.message || data.error || "Failed to load house detail",
        });
      }

      return { betId: String(betId), house: data.data };
    } catch (error) {
      return rejectWithValue({
        betId: String(betId),
        status: 0,
        message: `Network error: ${error.message}`,
      });
    }
  }
);

export const fetchShowcaseBets = createAsyncThunk(
  'betHouse/fetchShowcaseBets',
  async ({ page = 1, limit = 6 } = {}, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/bets/published?page=${page}&limit=${limit}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.error || 'Failed to fetch showcase bets');
      }

      return data;
    } catch (error) {
      return rejectWithValue(`Network error: ${error.message}`);
    }
  },
  {
    condition: ({ page = 1, limit = 6, force = false } = {}, { getState }) => {
      if (force) return true;
      if (isNetworkOffline()) return false;
      const state = getState().betHouse;
      return !shouldSkipCachedFeed({
        force,
        fetchedAt: state.lastFetchedAt,
        hasData: state.lastFetchedAt != null,
        page,
        limit,
        cachedPage: state.lastFetchPage,
        cachedLimit: state.lastFetchLimit,
      });
    },
  }
);

export const searchShowcaseBets = createAsyncThunk(
  'betHouse/searchShowcaseBets',
  async ({ query, page = 1, limit = 6 } = {}, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/bets/search?query=${encodeURIComponent(query)}&page=${page}&limit=${limit}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.error || 'Failed to search showcase bets');
      }

      return data;
    } catch (error) {
      return rejectWithValue(`Network error: ${error.message}`);
    }
  }
);

export const toggleFollowBetHouse = createAsyncThunk(
  "betHouse/toggleFollowBetHouse",
  async ({ userId, betId }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bets/follow`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, betId }),
      });
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.error || "Failed to update follow status");
      }
      return {
        betId,
        userId,
        followed: Boolean(data.followed),
        followersCount: Number(data.followersCount) || 0,
      };
    } catch (error) {
      return rejectWithValue(`Network error: ${error.message}`);
    }
  }
);

const initialState = {
  bets: [],
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  lastFetchPage: 1,
  lastFetchLimit: 6,
  pagination: {
    page: 1,
    limit: 6,
    totalPages: 0,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false
  },
  searchQuery: '',
  isSearchMode: false,
  searchResults: 0,
  followPending: false,
  followError: null,
  detailById: {},
  detailLoadingId: null,
  detailErrorById: {},
};

const betHouseSlice = createSlice({
  name: 'betHouse',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetSearch: (state) => {
      state.searchQuery = '';
      state.isSearchMode = false;
      state.searchResults = 0;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShowcaseBets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShowcaseBets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bets = action.payload.data || [];
        state.pagination = action.payload.pagination || state.pagination;
        state.error = null;
        const page = action.meta?.arg?.page ?? 1;
        const limit = action.meta?.arg?.limit ?? state.lastFetchLimit;
        if (page === 1) {
          state.lastFetchedAt = Date.now();
          state.lastFetchPage = page;
          state.lastFetchLimit = limit;
        }
      })
      .addCase(fetchShowcaseBets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        if (
          (!action.meta?.arg?.page || action.meta.arg.page === 1) &&
          state.bets.length === 0
        ) {
          state.bets = [];
        }
      })
      .addCase(searchShowcaseBets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchShowcaseBets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bets = action.payload.data || [];
        state.pagination = action.payload.pagination || state.pagination;
        state.searchQuery = action.payload.searchQuery || '';
        state.isSearchMode = true;
        state.searchResults = action.payload.searchResults || 0;
        state.error = null;
      })
      .addCase(searchShowcaseBets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.bets = [];
      })
      .addCase(toggleFollowBetHouse.pending, (state) => {
        state.followPending = true;
        state.followError = null;
      })
      .addCase(toggleFollowBetHouse.fulfilled, (state, action) => {
        state.followPending = false;
        const { betId, userId, followed } = action.payload;
        const bet = state.bets.find((b) => b?._id === betId);
        if (!bet) return;

        if (!Array.isArray(bet.followers)) bet.followers = [];
        const uid = String(userId);
        const idx = bet.followers.findIndex(
          (f) => String(f.user?._id || f.user) === uid
        );

        if (followed && idx === -1) {
          bet.followers.push({ user: userId, followedAt: new Date().toISOString() });
        } else if (!followed && idx !== -1) {
          bet.followers.splice(idx, 1);
        }
      })
      .addCase(toggleFollowBetHouse.rejected, (state, action) => {
        state.followPending = false;
        state.followError = action.payload;
      })
      .addCase(fetchBetHouseDetail.pending, (state, action) => {
        const betId = action.meta?.arg?.betId
          ? String(action.meta.arg.betId)
          : null;
        state.detailLoadingId = betId;
        if (betId) {
          state.detailErrorById[betId] = null;
        }
      })
      .addCase(fetchBetHouseDetail.fulfilled, (state, action) => {
        const { betId, house } = action.payload;
        const id = String(betId);
        state.detailLoadingId = null;
        if (id && house) {
          state.detailById[id] = house;
          state.detailErrorById[id] = null;
        }
      })
      .addCase(fetchBetHouseDetail.rejected, (state, action) => {
        const betId =
          action.payload?.betId != null
            ? String(action.payload.betId)
            : action.meta?.arg?.betId
              ? String(action.meta.arg.betId)
              : null;
        state.detailLoadingId = null;
        if (betId) {
          state.detailErrorById[betId] = action.payload;
        }
      });
  },
});

export const { clearError, resetSearch, setSearchQuery } = betHouseSlice.actions;

export const selectBetHouse = (state) => state.betHouse;
export const selectShowcaseBets = (state) => state.betHouse.bets;
export const selectBetHouseLoading = (state) => state.betHouse.isLoading;
export const selectBetHouseError = (state) => state.betHouse.error;
export const selectBetHousePagination = (state) => state.betHouse.pagination;
export const selectSearchQuery = (state) => state.betHouse.searchQuery;
export const selectIsSearchMode = (state) => state.betHouse.isSearchMode;
export const selectSearchResults = (state) => state.betHouse.searchResults;
export const selectFollowPending = (state) => state.betHouse.followPending;
export const selectBetHouseDetailById = (betId) => (state) => {
  const id = betId != null ? String(betId) : "";
  if (!id) return null;
  return state.betHouse.detailById[id] || null;
};
export const selectBetHouseDetailLoadingId = (state) =>
  state.betHouse.detailLoadingId;
export const selectBetHouseDetailError = (betId) => (state) => {
  const id = betId != null ? String(betId) : "";
  if (!id) return null;
  return state.betHouse.detailErrorById[id] || null;
};

export default betHouseSlice.reducer;
